import { validateEmail } from 'pages/contacts/utils'
import { v4 as uuidv4 } from 'uuid'
import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'

/**
 * SharedContact 에디터에서 사용되는 이벤트 데이터를 처리하는 유틸리티 함수들
 */

interface Photo {
  url?: string
  etag?: string | null
  file?: File
}

interface Person {
  prefix?: string
  suffix?: string
  nickname?: string
  first_name?: string
  last_name?: string
  middle_name?: string
  display_name?: string
  phonetic_first_name?: string
  phonetic_last_name?: string
}

interface Organization {
  company?: string
  department?: string
  office?: string
  job_title?: string
  phonetic_company?: string
}

interface EmailItem {
  type: string
  value: string
}

interface PhoneItem {
  type: string
  value: string
}

interface AddressItem {
  type: string
  city: string
  country: string
  po_box: string
  postalCode: string
  state: string
  street: string
}

interface UrlItem {
  type: string
  value: string
}

interface DateItem {
  type: string
  value: string
}

interface RelatedPersonItem {
  type: string
  value: string
}

interface CustomItem {
  type: string
  value: string
}

interface NoteItem {
  id?: string
  type?: string
  value: string
  updated_at?: string
}

interface Category {
  id: string | number
  name?: string
  color?: string
}

interface ContactEmail {
  type: string
  email: string
}

interface ContactPhone {
  type: string
  number: string
}

interface ContactAddress {
  type?: string
  city?: string
  country?: string
  po_box?: string
  postal_code?: string
  state?: string
  street?: string
}

interface ContactLink {
  type: string
  url?: string
}

interface ContactEventDate {
  type: string
  date?: string
}

interface ContactRelatedPerson {
  type: string
  name?: string
}

interface ContactCustom {
  label: string
  content?: string
}

interface ContactNote {
  id?: string
  type?: string
  content?: string
  updated_at?: string
}

interface Contact {
  id: string | null
  photo?: Photo | string | null
  person?: Person
  organization?: Organization
  emails?: ContactEmail[]
  phones?: ContactPhone[]
  addresses?: ContactAddress[]
  links?: ContactLink[]
  event_dates?: ContactEventDate[]
  related_persons?: ContactRelatedPerson[]
  customs?: ContactCustom[]
  notes?: ContactNote[]
  categories?: Category[]
}

interface EditData {
  id: string | null
  uid?: string | null
  etag?: string | null
  photo: Photo | string | null
  isPersonal?: boolean
  isChangedPhoto?: boolean

  prefix: string
  suffix: string
  nickname: string
  firstName: string
  lastName: string
  middleName: string
  displayName: string
  phoneticFirstName: string
  phoneticLastName: string
  phoneticMiddleName?: string

  company: string
  department: string
  office: string
  jobTitle: string
  phoneticCompany: string

  emails: EmailItem[]
  phones: PhoneItem[]
  addresses: AddressItem[]
  urls: UrlItem[]
  dates: DateItem[]
  related_persons: RelatedPersonItem[]
  customs: CustomItem[]
  notes: NoteItem[]
  categories: Category[]
  deleteNotes?: NoteItem[]
}

interface DetailDataEmail {
  type: string
  email: string
}

interface DetailDataPhone {
  type: string
  number: string
}

interface DetailDataAddress {
  type?: string
  po_box?: string
  street?: string
  city?: string
  state?: string
  post_code?: string
  country?: string
}

interface DetailDataLink {
  type: string
  url?: string
}

interface DetailDataDate {
  type: string
  date: string
}

interface DetailDataRelatedPerson {
  type: string
  name?: string
}

interface DetailData {
  id?: string | null
  uid?: string | null
  etag?: string | null
  isPersonal?: boolean
  sharedContactData?: {
    photo?: string | Photo | null
  } | null
  photoUrl?: string | null
  prefix?: string | null
  suffix?: string | null
  nickname?: string | null
  firstName?: string | null
  lastName?: string | null
  middleName?: string | null
  displayName?: string | null
  yomiFirstName?: string | null
  yomiLastName?: string | null
  yomiMiddleName?: string | null
  company?: string | null
  department?: string | null
  position?: string | null
  emails?: DetailDataEmail[]
  phones?: DetailDataPhone[]
  addresses?: DetailDataAddress[]
  links?: DetailDataLink[]
  dates?: DetailDataDate[]
  relatedPersons?: DetailDataRelatedPerson[]
  customFields?: CustomItem[]
  notes?: ContactNote[]
  hasPhoto?: boolean
}

interface SaveDataNote {
  action: 'add' | 'update' | 'delete'
  id?: string
  type?: string
  content?: string
  updated_at?: string
}

interface SaveData {
  id: string | null
  photo: Photo | null
  person: Person
  organization: Organization
  emails?: ContactEmail[]
  phones?: ContactPhone[]
  addresses?: ContactAddress[]
  links?: ContactLink[]
  event_dates?: ContactEventDate[]
  related_persons?: ContactRelatedPerson[]
  customs?: ContactCustom[]
  notes?: SaveDataNote[]
  categories: Category[]
  favorite?: boolean
}

interface ValidationErrors {
  name: boolean
  emails: number[]
}

interface ValidationResult {
  isValid: boolean
  errors?: ValidationErrors
}

/**
 * SharedContact 데이터를 편집용 데이터 형식으로 변환
 * @param {Object} contact - 서버에서 받은 SharedContact 객체
 * @returns {Object} 에디터에서 사용할 수 있는 형식으로 변환된 데이터
 */
export const createEditData = (contact: Contact | null): EditData => {
  if (!contact) {
    return {
      id: null,
      photo: null,

      prefix: '',
      suffix: '',
      nickname: '',
      firstName: '',
      lastName: '',
      middleName: '',
      displayName: '',
      phoneticFirstName: '',
      phoneticLastName: '',

      company: '',
      department: '',
      office: '',
      jobTitle: '',
      phoneticCompany: '',

      emails: [{ type: 'work', value: '' }],
      phones: [{ type: 'mobile', value: '' }],
      addresses: [],
      urls: [],
      dates: [],
      related_persons: [],
      customs: [],
      notes: [],
      categories: [],
    }
  }

  return {
    id: contact.id,
    photo: contact.photo || null,

    prefix: contact.person?.prefix || '',
    suffix: contact.person?.suffix || '',
    nickname: contact.person?.nickname || '',
    firstName: contact.person?.first_name || '',
    lastName: contact.person?.last_name || '',
    middleName: contact.person?.middle_name || '',
    displayName: contact.person?.display_name || '',
    phoneticFirstName: contact.person?.phonetic_first_name || '',
    phoneticLastName: contact.person?.phonetic_last_name || '',

    company: contact.organization?.company || '',
    department: contact.organization?.department || '',
    office: contact.organization?.office || '',
    jobTitle: contact.organization?.job_title || '',
    phoneticCompany: contact.organization?.phonetic_company || '',

    emails:
      contact.emails && contact.emails.length > 0
        ? contact.emails.map((email) => ({ type: email.type, value: email.email ?? '' }))
        : [{ type: 'work', value: '' }],
    phones:
      contact.phones && contact.phones.length > 0
        ? contact.phones.map((phone) => ({ type: phone.type, value: phone.number ?? '' }))
        : [{ type: 'mobile', value: '' }],
    addresses:
      contact.addresses && contact.addresses.length > 0
        ? contact.addresses.map((address) => ({
            type: address.type || '',
            city: address.city || '',
            country: address.country || '',
            po_box: address.po_box || '',
            postalCode: address.postal_code || '',
            state: address.state || '',
            street: address.street || '',
          }))
        : [],
    urls:
      contact.links && contact.links.length > 0
        ? contact.links.map((link) => ({ type: link.type, value: link.url || '' }))
        : [],
    dates:
      contact.event_dates && contact.event_dates.length > 0
        ? contact.event_dates.map((date) => ({ type: date.type, value: date.date || '' }))
        : [],
    related_persons:
      contact.related_persons && contact.related_persons.length > 0
        ? contact.related_persons.map((related_person) => ({
            type: related_person.type,
            value: related_person.name || '',
          }))
        : [],
    customs:
      contact.customs && contact.customs.length > 0
        ? contact.customs.map((custom) => ({
            type: custom.label,
            value: custom.content || '',
          }))
        : [],
    notes:
      contact.notes && contact.notes.length > 0
        ? contact.notes.map((note) => ({
            id: note.id,
            type: note.type,
            value: note.content || '',
            updated_at: note.updated_at,
          }))
        : [],
    categories: contact.categories && contact.categories.length > 0 ? contact.categories : [],
  }
}

export const createEditDataFromDetailData = (detailData: DetailData | any): EditData => {
  const d: EditData = {
    id: null,
    photo: null,

    prefix: '',
    suffix: '',
    nickname: '',
    firstName: '',
    lastName: '',
    middleName: '',
    displayName: '',
    phoneticFirstName: '',
    phoneticLastName: '',
    phoneticMiddleName: '',

    company: '',
    department: '',
    office: '',
    jobTitle: '',
    phoneticCompany: '',

    emails: [{ type: 'work', value: '' }],
    phones: [{ type: 'mobile', value: '' }],
    addresses: [],
    urls: [],
    dates: [],
    related_persons: [],
    customs: [],
    notes: [],
    categories: [],
  }

  d.isPersonal = detailData.isPersonal || false
  if (d.isPersonal) {
    d.id = detailData.id || null
    d.uid = detailData.uid || null
    d.etag = detailData.etag || null
  }
  d.photo = detailData.sharedContactData?.photo || detailData.photoUrl || null
  d.prefix = detailData.prefix || ''
  d.suffix = detailData.suffix || ''
  d.nickname = detailData.nickname || ''
  d.firstName = detailData.firstName || ''
  d.lastName = detailData.lastName || ''
  d.middleName = detailData.middleName || ''
  d.displayName = detailData.displayName || ''
  d.phoneticFirstName = detailData.yomiFirstName || ''
  d.phoneticLastName = detailData.yomiLastName || ''
  d.phoneticMiddleName = detailData.yomiMiddleName || ''

  d.company = detailData.company || ''
  d.department = detailData.department || ''
  d.jobTitle = detailData.position || ''

  d.emails =
    detailData.emails && detailData.emails.length > 0
      ? detailData.emails.map((email: DetailDataEmail) => ({
          type: email.type,
          value: email.email ?? '',
        }))
      : [{ type: 'work', value: '' }]
  d.phones =
    detailData.phones && detailData.phones.length > 0
      ? detailData.phones.map((phone: DetailDataPhone) => ({
          type: phone.type,
          value: phone.number ?? '',
        }))
      : [{ type: 'mobile', value: '' }]
  d.addresses =
    detailData.addresses && detailData.addresses.length > 0
      ? detailData.addresses.map((address) => ({
          type: address.type || '',
          po_box: address.po_box || '',
          street: address.street || '',
          city: address.city || '',
          state: address.state || '',
          postalCode: address.post_code || '',
          country: address.country || '',
        }))
      : []
  d.urls =
    detailData.links && detailData.links.length > 0
      ? detailData.links.map((link) => ({ type: link.type, value: link.url || '' }))
      : []
  d.dates =
    detailData.dates && detailData.dates.length > 0
      ? detailData.dates.map((date: DetailDataDate) => ({
          type: date.type,
          value: date.date ?? '',
        }))
      : []
  d.related_persons =
    detailData.relatedPersons && detailData.relatedPersons.length > 0
      ? detailData.relatedPersons.map((related_person) => ({
          type: related_person.type,
          value: related_person.name || '',
        }))
      : []
  d.customs = detailData.customFields || []
  d.notes =
    detailData.notes && detailData.notes.length > 0
      ? detailData.notes.map((note) => ({
          id: note.id,
          type: note.type,
          value: note.content || '',
          updated_at: note.updated_at,
        }))
      : detailData.isPersonal
        ? [{ value: '' }]
        : []

  return d
}

/**
 * 편집 데이터를 저장용 형식으로 변환
 * @param {Object} editData - 에디터에서 편집된 데이터
 * @returns {Object} 서버에 저장할 형식의 데이터
 */
export const createSaveData = (editData: EditData): SaveData => {
  const stringFilter = (str: string | null | undefined): string | undefined => {
    const trimmed = str?.trim()
    if (trimmed == null || trimmed === '') return undefined
    return trimmed
  }

  let notes: SaveDataNote[] | undefined = undefined
  if (editData.notes && editData.notes.length > 0) {
    notes = editData.notes.map((note) => {
      const content = stringFilter(note.value)
      if (content == null || content.length === 0) {
        return {
          action: 'delete' as const,
          id: note.id,
        }
      }
      return {
        action: (note.id ? 'update' : 'add') as 'update' | 'add',
        id: note.id ? note.id : undefined,
        type: note.type || 'text',
        content: stringFilter(note.value),
        updated_at: note.updated_at ? note.updated_at : undefined,
      }
    })
  }
  if (editData.deleteNotes && editData.deleteNotes.length > 0) {
    const deletedNotes: SaveDataNote[] = editData.deleteNotes.map((note) => ({
      action: 'delete' as const,
      id: note.id,
    }))
    if (notes) {
      notes.push(...deletedNotes)
    } else {
      notes = deletedNotes
    }
  }

  return {
    id: editData.id,
    photo:
      typeof editData.photo === 'object' && editData.photo && 'url' in editData.photo
        ? { url: editData.photo.url, etag: null }
        : null,

    person: {
      prefix: stringFilter(editData.prefix),
      suffix: stringFilter(editData.suffix),
      nickname: stringFilter(editData.nickname),
      first_name: stringFilter(editData.firstName),
      last_name: stringFilter(editData.lastName),
      middle_name: stringFilter(editData.middleName),
      display_name: stringFilter(editData.displayName),
      phonetic_first_name: stringFilter(editData.phoneticFirstName),
      phonetic_last_name: stringFilter(editData.phoneticLastName),
    },
    organization: {
      company: stringFilter(editData.company),
      department: stringFilter(editData.department),
      office: stringFilter(editData.office),
      job_title: stringFilter(editData.jobTitle),
      phonetic_company: stringFilter(editData.phoneticCompany),
    },
    emails:
      editData.emails.length === 0
        ? undefined
        : editData.emails
            .filter((email) => email.value?.trim() !== '')
            .map((email) => ({
              type: email.type,
              email: stringFilter(email.value)!,
            })),
    phones:
      editData.phones.length === 0
        ? undefined
        : editData.phones
            .filter((phone) => phone.value?.trim() !== '')
            .map((phone) => ({
              type: phone.type,
              number: stringFilter(phone.value)!,
            })),
    addresses:
      editData.addresses.length === 0
        ? undefined
        : editData.addresses
            .filter(
              (address) =>
                address.street?.trim() !== '' ||
                address.city?.trim() !== '' ||
                address.country?.trim() !== '' ||
                address.postalCode?.trim() !== '' ||
                address.state?.trim() !== '' ||
                address.po_box?.trim() !== '',
            )
            .map((address) => ({
              type: address.type,
              city: stringFilter(address.city),
              country: stringFilter(address.country),
              po_box: stringFilter(address.po_box),
              postal_code: stringFilter(address.postalCode),
              state: stringFilter(address.state),
              street: stringFilter(address.street),
            })),
    links:
      editData.urls.length === 0
        ? undefined
        : editData.urls
            .filter((url) => url.value?.trim() !== '')
            .map((url) => ({
              type: url.type,
              url: stringFilter(url.value),
            })),
    event_dates:
      editData.dates.length === 0
        ? undefined
        : editData.dates
            .filter((date) => date.value?.trim() !== '')
            .map((date) => ({
              type: date.type,
              date: stringFilter(date.value),
            })),
    related_persons:
      editData.related_persons.length === 0
        ? undefined
        : editData.related_persons
            .filter((related_person) => related_person.value?.trim() !== '')
            .map((related_person) => ({
              type: related_person.type,
              name: related_person.value,
            })),
    customs:
      editData.customs.length === 0
        ? undefined
        : editData.customs
            .filter((custom) => custom.value?.trim() !== '')
            .map((custom) => ({
              label: custom.type,
              content: stringFilter(custom.value),
            })),
    notes,
    categories: editData.categories.length === 0 ? [] : editData.categories,
  }
}

/**
 * 데이터가 유효한지 검사
 * @param {Object} editData - 검사할 편집 데이터
 * @returns {Object} { isValid: boolean, errors: Object } 유효성 검사 결과
 */
export const validateEditData = (editData: EditData): ValidationResult => {
  const errors: ValidationErrors = { name: false, emails: [] }

  if (editData.isPersonal) {
    return { isValid: true }
  }

  // 필수 필드 검사
  // if (!editData.firstName?.trim() && !editData.lastName?.trim()) {
  //   errors.name = true
  // }

  editData.emails.forEach((email, index) => {
    if (email?.value?.trim() && !validateEmail(email?.value?.trim())) {
      errors.emails.push(index)
    }
  })

  return {
    isValid: errors.emails.length === 0,
    errors,
  }
}

/**
 * 두 편집 데이터를 비교하여 변경 여부 확인
 * @param {Object} original - 원본 데이터
 * @param {Object} current - 현재 데이터
 * @returns {boolean} 데이터 변경 여부
 */
export const isDataModified = (original: EditData | null, current: EditData): boolean => {
  if (!original) return true

  // 사진 변경 여부 확인
  if (current.isChangedPhoto) return true

  // 기본 인적 정보 필드 비교
  const personFields: (keyof EditData)[] = [
    'prefix',
    'suffix',
    'nickname',
    'firstName',
    'lastName',
    'middleName',
    'displayName',
    'phoneticFirstName',
    'phoneticLastName',
  ]
  for (const field of personFields) {
    const o = original[field] || ''
    const c = current[field] || ''
    if (o !== c) return true
  }

  // 조직 정보 필드 비교
  const organizationFields: (keyof EditData)[] = [
    'company',
    'department',
    'office',
    'jobTitle',
    'phoneticCompany',
  ]
  for (const field of organizationFields) {
    const o = original[field] || ''
    const c = current[field] || ''
    if (o !== c) return true
  }

  const originalPhotoUrl =
    typeof original.photo === 'object' && original.photo && 'url' in original.photo
      ? original.photo.url
      : original.photo
  const currentPhotoUrl =
    typeof current.photo === 'object' && current.photo && 'url' in current.photo
      ? current.photo.url
      : current.photo
  if (originalPhotoUrl !== currentPhotoUrl) return true

  // 배열 필드 비교 (깊은 비교)
  const arrayFields: (keyof EditData)[] = [
    'emails',
    'phones',
    'addresses',
    'urls',
    'dates',
    'related_persons',
    'customs',
    'notes',
  ]

  for (const field of arrayFields) {
    const originalArray = (original[field] as any[]) || []
    const currentArray = (current[field] as any[]) || []

    if (originalArray.length !== currentArray.length) return true

    // 각 배열 항목의 타입과 값 비교
    for (let i = 0; i < originalArray.length; i++) {
      const origItem = originalArray[i]
      const currItem = currentArray[i]

      // 객체 키 비교
      const keys = Object.keys({ ...origItem, ...currItem })
      for (const key of keys) {
        if (origItem[key] !== currItem[key]) return true
      }
    }
  }

  // 카테고리 비교
  const originalCategories = original.categories || []
  const currentCategories = current.categories || []

  if (originalCategories.length !== currentCategories.length) return true

  // 카테고리 ID 기준으로 비교
  const origCategoryIds = new Set(originalCategories.map((cat) => cat.id))
  const currCategoryIds = new Set(currentCategories.map((cat) => cat.id))

  if (origCategoryIds.size !== currCategoryIds.size) return true

  for (const id of Array.from(origCategoryIds)) {
    if (!currCategoryIds.has(id)) return true
  }

  return false
}

export const createVCardData = (editData: EditData): string => {
  let vCardStr =
    'BEGIN:VCARD\r\nVERSION:3.0\r\nPRODID:-//NextIntelligence.ai//OfficeMail WA//EN\r\n'

  const nStr =
    'N:' +
    (editData.lastName ? encodeMIMEValue(editData.lastName) : '') +
    ';' +
    (editData.firstName ? encodeMIMEValue(editData.firstName) : '') +
    ';' +
    (editData.middleName ? encodeMIMEValue(editData.middleName) : '') +
    ';' +
    (editData.prefix ? encodeMIMEValue(editData.prefix) : '') +
    ';' +
    (editData.suffix ? encodeMIMEValue(editData.suffix) : '')
  vCardStr += `${nStr}\r\n`

  let fnValue: string | null = null
  const nameObj = new NameObj({
    prefix: editData.prefix,
    first_name: editData.firstName,
    last_name: editData.lastName,
    middle_name: editData.middleName,
    suffix: editData.suffix,
  })
  const nameSplitter = new NameSplitter(navigator.language || 'en-US')
  nameSplitter.guessFullNameStyle(nameObj)
  fnValue = nameSplitter.joinWithName(nameObj, true, true)

  if (fnValue == null || fnValue.length === 0) {
    fnValue =
      (editData.firstName ? encodeMIMEValue(editData.firstName) : '') +
      (editData.middleName ? ' ' + encodeMIMEValue(editData.middleName) : '') +
      (editData.lastName ? ' ' + encodeMIMEValue(editData.lastName) : '')
  }
  vCardStr += `FN:${fnValue}\r\n`

  if (editData.nickname) {
    vCardStr += `NICKNAME:${editData.nickname ? encodeMIMEValue(editData.nickname) : ''}\r\n`
  }

  if (editData.phoneticFirstName) {
    vCardStr += `X-PHONETIC-FIRST-NAME:${editData.phoneticFirstName ? encodeMIMEValue(editData.phoneticFirstName) : ''}\r\n`
  }
  if (editData.phoneticLastName) {
    vCardStr += `X-PHONETIC-LAST-NAME:${editData.phoneticLastName ? encodeMIMEValue(editData.phoneticLastName) : ''}\r\n`
  }
  if (editData.phoneticMiddleName) {
    vCardStr += `X-PHONETIC-MIDDLE-NAME:${editData.phoneticMiddleName ? encodeMIMEValue(editData.phoneticMiddleName) : ''}\r\n`
  }

  if (editData.company || editData.department) {
    const orgStr =
      'ORG:' +
      (editData.company ? encodeMIMEValue(editData.company) : '') +
      ';' +
      (editData.department ? encodeMIMEValue(editData.department) : '')
    vCardStr += `${orgStr}\r\n`
  }

  if (editData.jobTitle) {
    vCardStr += `TITLE:${editData.jobTitle ? encodeMIMEValue(editData.jobTitle) : ''}\r\n`
  }

  if (editData.uid) {
    vCardStr += `UID:${editData.uid}\r\n`
  } else {
    vCardStr += `UID:${uuidv4()}\r\n`
  }

  const emailTypes = ['work', 'home', 'other']
  let groupId = 0

  editData.emails.forEach((email) => {
    if (!email.value || email.value.length === 0) return
    const type = email.type
    const value = encodeMIMEValue(email.value)
    if (!type || type.length === 0) {
      vCardStr += `EMAIL:${value}\r\n`
    } else if (emailTypes.includes(type)) {
      vCardStr += `EMAIL;TYPE=${type}:${value}\r\n`
    } else {
      const groupTag = 'item' + groupId++
      vCardStr += `${groupTag}.EMAIL:${value}\r\n`
      vCardStr += `${groupTag}.X-ABLabel:${type}\r\n`
    }
  })

  const addressTypes = ['work', 'home', 'other']
  editData.addresses.forEach((address) => {
    const type = address.type
    const value =
      (address.po_box ? encodeMIMEValue(address.po_box) : '') +
      ';;' +
      (address.street ? encodeMIMEValue(address.street) : '') +
      ';' +
      (address.city ? encodeMIMEValue(address.city) : '') +
      ';' +
      (address.state ? encodeMIMEValue(address.state) : '') +
      ';' +
      (address.postalCode ? encodeMIMEValue(address.postalCode) : '') +
      ';' +
      (address.country ? encodeMIMEValue(address.country) : '')

    if (!type || type.length === 0) {
      vCardStr += `ADR:${value}\r\n`
    } else if (addressTypes.includes(type)) {
      vCardStr += `ADR;TYPE=${type}:${value}\r\n`
    } else {
      const groupTag = 'item' + groupId++
      vCardStr += `${groupTag}.ADR:${value}\r\n`
      vCardStr += `${groupTag}.X-ABLabel:${type}\r\n`
    }
  })

  let addedAnniversaryCount = 0
  editData.dates.forEach((date) => {
    const value = date.value ? encodeMIMEValue(date.value) : ''
    if (date.type === 'birthday') {
      vCardStr += `BDAY;TYPE=DATE:${value}\r\n`
    } else if (date.type === 'anniversary' && addedAnniversaryCount === 0) {
      vCardStr += `ANNIVERSARY;TYPE=DATE:${value}\r\n`
      addedAnniversaryCount++
    } else {
      let t = date.type
      if (t === 'other') {
        t = '_$!<Other>!$_'
      }
      const groupTag = 'item' + groupId++
      vCardStr += `${groupTag}.X-ABLabel:${t}\r\n`
      vCardStr += `${groupTag}.X-ABDate:${value}\r\n`
    }
  })

  const urlTypes = [
    'website',
    'linkedin',
    'facebook',
    'github',
    'skype',
    'twitter',
    'whatsapp',
    'instagram',
    'other',
  ]
  editData.urls.forEach((url) => {
    const type = url.type
    const value = url.value ? encodeMIMEValue(url.value) : ''
    if (!type || type.length === 0) {
      vCardStr += `URL:${value}\r\n`
    } else if (urlTypes.includes(type)) {
      vCardStr += `URL;TYPE=${type}:${value}\r\n`
    } else {
      const groupTag = 'item' + groupId++
      vCardStr += `${groupTag}.URL:${value}\r\n`
      vCardStr += `${groupTag}.X-ABLabel:${type}\r\n`
    }
  })

  const phoneTypes = ['work', 'home', 'mobile', 'cell', 'fax', 'pager', 'other']
  editData.phones.forEach((phone) => {
    if (!phone.value || phone.value.length === 0) return

    const type = phone.type
    const value = phone.value ? encodeMIMEValue(phone.value) : ''
    if (!type || type.length === 0) {
      vCardStr += `TEL:${value}\r\n`
    } else if (phoneTypes.includes(type)) {
      vCardStr += `TEL;TYPE=${type}:${value}\r\n`
    } else {
      const groupTag = 'item' + groupId++
      vCardStr += `${groupTag}.TEL:${value}\r\n`
      vCardStr += `${groupTag}.X-ABLabel:${type}\r\n`
    }
  })

  const relatedPersonTypes = [
    'spouse',
    'child',
    'mother',
    'father',
    'parent',
    'brother',
    'sister',
    'friend',
    'relative',
    'manager',
    'assistant',
    'referredby',
    'partner',
    'domesticpartner',
  ]
  editData.related_persons.forEach((related_person) => {
    const type = related_person.type
    const value = related_person.value
    let xType: string | null = null
    if (!type || type.length === 0) {
    } else if (relatedPersonTypes.includes(type)) {
      xType = '_$!<' + type.charAt(0).toUpperCase() + type.slice(1) + '>!$_'
    } else {
      xType = type
    }

    if (xType) {
      const groupTag = 'item' + groupId++
      vCardStr += `${groupTag}.X-ABRELATEDNAMES:${value}\r\n`
      vCardStr += `${groupTag}.X-ABLabel:${xType}\r\n`
    } else {
      vCardStr += `X-ABRELATEDNAMES:${value}\r\n`
    }
  })

  const note = editData.notes.length > 0 ? editData.notes[0] : null
  if (note && note.value && note.value.trim() !== '') {
    const value = note.value ? encodeMIMEValue(note.value) : ''
    vCardStr += `NOTE:${value}\r\n`
  }

  let photo: string | null = null
  if (typeof editData.photo === 'object' && editData.photo && 'url' in editData.photo) {
    photo = editData.photo.url?.replace(/^data:image\/.*;base64,/, '') || null
  } else if (typeof editData.photo === 'string') {
    photo = editData.photo
  }
  if (photo) {
    const mimeType = mimeTypeForData(photo)
    vCardStr = vCardStr.concat(`PHOTO;ENCODING=b;TYPE=${mimeType}:${photo}\r\n`)
  }

  //TODO: Custom Fields

  vCardStr += `END:VCARD\r\n`
  return vCardStr
}

const mimeTypeForData = (data: string): string => {
  if (!data || data.length < 2) return ''

  const c = data.substring(0, 2)
  const decoded = window.atob(c).charCodeAt(0)

  switch (decoded) {
    case 0xff:
      return 'jpeg' // 'image/jpeg'
    case 0x89:
      return 'png' // 'image/png'
    case 0x47:
      return 'gif' // 'image/gif'
    case 0x49:
    case 0x4d:
      return 'tiff' // 'image/tiff'
    case 0x25:
      return 'pdf' // 'application/pdf'
    case 0xd0:
      return 'vnd' // 'application/vnd'
  }
  return ''
}

const encodeMIMEValue = (value: string): string => {
  let retValue = ''

  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    if (char === '\n') {
      retValue += '\\n'
    } else if (char === '\r') {
      retValue += '\\r'
    } else if (char === ';') {
      retValue += '\\;'
    } else {
      retValue += char
    }
  }
  return retValue
}

export const mergeContactEditData = (orgEditData: EditData, newEditData: EditData): EditData => {
  const n = newEditData
  const m: EditData = { ...orgEditData }

  if (n.photo) m.photo = n.photo
  if (n.prefix) m.prefix = n.prefix
  if (n.suffix) m.suffix = n.suffix
  if (n.nickname) m.nickname = n.nickname
  if (n.firstName) m.firstName = n.firstName
  if (n.lastName) m.lastName = n.lastName
  if (n.middleName) m.middleName = n.middleName
  if (n.displayName) m.displayName = n.displayName
  if (n.phoneticFirstName) m.phoneticFirstName = n.phoneticFirstName
  if (n.phoneticLastName) m.phoneticLastName = n.phoneticLastName
  if (n.phoneticMiddleName) m.phoneticMiddleName = n.phoneticMiddleName
  if (n.company) m.company = n.company
  if (n.department) m.department = n.department
  if (n.jobTitle) m.jobTitle = n.jobTitle

  if (n.emails.length > 0) {
    const newEmails = [...m.emails]
    n.emails.forEach((email) => {
      const mEmail = newEmails.find((nEmail) => {
        return email.value.toLowerCase() === nEmail.value.toLowerCase()
      })
      if (mEmail) {
        mEmail.type = email.type
        mEmail.value = email.value
      } else {
        newEmails.push(email)
      }
    })
    m.emails = newEmails
  }

  if (n.phones.length > 0) {
    const newPhones = [...m.phones]
    n.phones.forEach((phone) => {
      const mPhone = newPhones.find((nPhone) => {
        return phone.value.toLowerCase() === nPhone.value.toLowerCase()
      })
      if (mPhone) {
        mPhone.type = phone.type
        mPhone.value = phone.value
      } else {
        newPhones.push(phone)
      }
    })
    m.phones = newPhones
  }

  if (n.addresses.length > 0) {
    const newAddresses = [...m.addresses]
    n.addresses.forEach((address) => {
      const nAddr = JSON.stringify(address)
      const mAddr = newAddresses.find((mAddress) => {
        return nAddr === JSON.stringify(mAddress)
      })
      if (mAddr == null) {
        newAddresses.push(address)
      }
    })
    m.addresses = newAddresses
  }

  if (n.urls.length > 0) {
    const newUrls = [...m.urls]
    n.urls.forEach((url) => {
      const mUrl = newUrls.find((nUrl) => {
        return url.value.toLowerCase() === nUrl.value.toLowerCase()
      })
      if (mUrl) {
        mUrl.type = url.type
        mUrl.value = url.value
      } else {
        newUrls.push(url)
      }
    })
    m.urls = newUrls
  }

  if (n.dates.length > 0) {
    const newDates = [...m.dates]
    n.dates.forEach((date) => {
      const mDate = newDates.find((nDate) => {
        return (
          date.type.toLowerCase() === nDate.type.toLowerCase() &&
          date.value.toLowerCase() === nDate.value.toLowerCase()
        )
      })
      if (mDate) {
        mDate.type = date.type
        mDate.value = date.value
      } else {
        newDates.push(date)
      }
    })
    m.dates = newDates
  }

  if (n.related_persons.length > 0) {
    const newRelatedPersons = [...m.related_persons]
    n.related_persons.forEach((related_person) => {
      const mRelatedPerson = newRelatedPersons.find((nRelatedPerson) => {
        return (
          related_person.type.toLowerCase() === nRelatedPerson.type.toLowerCase() &&
          related_person.value.toLowerCase() === nRelatedPerson.value.toLowerCase()
        )
      })
      if (mRelatedPerson) {
        mRelatedPerson.type = related_person.type
        mRelatedPerson.value = related_person.value
      } else {
        newRelatedPersons.push(related_person)
      }
    })
    m.related_persons = newRelatedPersons
  }

  if (n.customs && n.customs.length > 0) {
    m.customs = [...m.customs, ...n.customs]
  }

  if (orgEditData.isPersonal) {
    if (n.notes.length > 0 && n.notes[0].value && n.notes[0].value.length > 0) {
      m.notes = n.notes
    }
  } else {
    if (n.notes.length > 0) {
      const newNotes = [...m.notes]
      n.notes.forEach((note) => {
        const mNote = newNotes.find((nNote) => {
          return (
            note.type?.toLowerCase() === nNote.type?.toLowerCase() &&
            note.value.toLowerCase() === nNote.value.toLowerCase()
          )
        })
        if (mNote) {
          mNote.id = note.id
          mNote.type = note.type
          mNote.value = note.value
          mNote.updated_at = note.updated_at
        } else {
          newNotes.push(note)
        }
      })
      m.notes = newNotes
    }
  }

  return m
}
